import { util } from 'echarts/core';

export type Token = { [token: string]: unknown };

export type OptionToken = { [key: string]: unknown };

/**
 * UI稿地址
 * https://frontend.myhexin.com/design-resource/access?jira=UED-9356&type=UI
 * key为UI稿上token名称去掉$，转为驼峰的形式
 * value为该token影响的主题配置
 */
const tokenMap = {
  // 轴label字体、柱子label字体、tooltip时间文本字体、tooltip的value的字体
  fontFamilyNumber: [
    'fontFamilyList.axisLabel',
    'fontFamilyList.barLabel',
    'fontFamilyList.tooltipTimeText',
    'fontFamilyList.tooltipValue'
  ],
  // 轴label字号、tooltip时间文本字号
  fontSizeLabel: [
    'fontSizeList.axisLabel',
    'fontSizeList.seriesLabel',
    'fontSizeList.tooltipTimeText'
  ],
  // 轴label字重、tooltip时间文本字重、tooltip的value的字重
  fontWeightMedium: [
    'fontWeightList.axisLabel',
    'fontWeightList.tooltipTimeText',
    'fontWeightList.tooltipValue'
  ],
  // 轴label颜色、图例label颜色
  colorTextSecondary: ['color.axisLabel', 'color.legendLabel'],
  // y轴分割线颜色
  colorVisualizationDivider: ['lineStyle.splitLineColor'],
  // y轴分割线线宽
  sizingHeightDivider: ['lineStyle.splitLineWidth'],
  // y = 0分割线的颜色
  colorVisualizationZeroAxes: ['lineStyle.zeroAxesColor'],
  // y = 0分割线的线宽
  sizingHeightZeroAxes: ['lineStyle.zeroAxesWidth'],
  // 图例大小、tooltip中的图例大小
  sizingSquareExtraSmall: [],
  // 图例label字号、tooltip的label字号、tooltip的value字号
  fontSizeBodySmall: ['fontSizeList.tooltipLabel', 'fontSizeList.tooltipValue'],
  // 图例label字重、tooltip的label的自重
  fontWeightRegular: ['fontWeightList.tooltipLabel'],
  /** *********************** 重复的token *********************** */
  // grid左内边距、grid右内边距
  paddingCardRightBase: ['gridStyle.left', 'gridStyle.right'],
  // grid左内边距、grid右内边距
  paddingCardRightLoose: ['gridStyle.left', 'gridStyle.right'],
  /** ********************************************************** */
  // grid上内边距、tooltip内边距
  paddingBase: ['gridStyle.top', 'tooltip.padding'],
  // grid下内边距、dataZoom的下外边距
  paddingExtraLoose: ['gridStyle.bottom', ''],
  // axisLabel的上下外边距，图例的上下外边距
  paddingTight: [],
  // 图例到图表区域的距离
  marginBase: [],
  // TODO
  marginNone: [],
  // 图例label的左边距、柱子label与柱子的间距2px
  marginExtraTight: [],
  // y轴label的宽度
  paddingSuperLoose: ['yAxis.axisLabel.width'],
  // Series颜色
  colorVisualizationPrimary: ['bar.seriesColor', 'dvRadar.seriesColorPrimary'],
  colorVisualization01: ['dvRadar.seriesColor01'],
  colorVisualization02: ['dvRadar.seriesColor02'],
  /** *********************** 重复的token *********************** */
  // 柱子圆角
  radiusNone: ['bar.radius'],
  radiusExtraSmall: ['bar.radius'],
  radiusSmall: ['bar.radius'],
  /** ********************************************************** */
  // dataZoom的上外边距
  marginBaseLoose: [],
  // axispointer的光标线的颜色
  colorVisualizationCross: ['lineStyle.axisPointerLineColor'],
  // axispointer的光标线的线宽
  sizingWidthCross: ['lineStyle.axisPointerLineWidth'],
  // tooltip背景色
  colorVisualizationTooltip: ['tooltip.backgroundColor'],
  // tooltip圆角
  radiusMedium: ['tooltip.radius'],
  // tooltip的label的颜色
  colorTextInversePrimary: ['color.tooltipLabel', 'color.tooltipValue'],
  // tooltip时间文本颜色
  colorTextInverseSecondary: ['color.tooltipTimeText'],
  // tooltip涨价value颜色
  colorPriceUp: ['color.priceUp'],
  // tooltip跌价value颜色
  colorPriceDown: ['color.priceDown'],
  // tooltip文字间距以及行间距
  marginTight: ['tooltip.margin'],
  /** *********************** 雷达图的token *********************** */
  radarSysShape: ['dvRadar.shape'],
  radarSysLine: ['dvRadar.axisLine'],
  radarSysArea: ['dvRadar.splitArea'],
  radarSysName: ['dvRadar.name'],
  radarSysRadius: ['dvRadar.radius'],
  radarLineStyle: ['dvRadar.lineStyle'],
  radarAreaStyle: ['dvRadar.areaStyle']
} as const;

function setAttribute(target, attrs: string[], value: any) {
  attrs.reduce((prev, cur, i) => {
    if (i === attrs.length - 1) {
      prev[cur] = value;
    } else if (prev[cur] === undefined || prev[cur] === null) {
      prev[cur] = {};
    }
    return prev[cur];
  }, target);
}

export function parseToken(tokens: Token) {
  const result = {} as OptionToken;

  for (const key in tokens) {
    if (!tokenMap.hasOwnProperty(key)) {
      console.warn(`${key} is not a valid token`);
      continue;
    }
    const value = tokens[key];
    tokenMap[key].forEach(item => {
      setAttribute(result, item.split('.'), value);
    });
  }

  return result;
}

export function mergeOptionToken(target: OptionToken, source: OptionToken) {
  if (util.isObject(target) && util.isObject(source)) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        const targetProp = target[key];
        const sourceProp = source[key];
        // eslint-disable-next-line max-depth
        if (
          util.isObject(sourceProp) &&
          util.isObject(targetProp) &&
          !util.isArray(sourceProp) &&
          !util.isArray(targetProp)
        ) {
          mergeOptionToken(targetProp, sourceProp);
        } else {
          target[key] = source[key];
        }
      }
    }
  }
  return target;
}
